import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Primereact components
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';

// Column
import { OnlineRefresherCourseColumnsList } from '../../../appconfig/DatatableSetting';

// Data table
import { defaultRowOptions, defaultPageRowOptions, Skeletonitems, SkeletonbodyTemplate, paginatorLinks, showingEntries, CourseStatusOptions, ActiveInactiveOptions } from '../../../appconfig/Settings';

import { RefresherCourseTab } from '../RefresherCourseTab';
import { deleteData, getAllDetails } from '../../../service/ProductService';
import { DELETE_REFRESHER_COURSE, GET_REFRESHER_COURSE_LIST } from '../../../config/ApiConstant';
import { courseEndTemplate, courseIdTemplate, courseNameTemplate, courseStartTemplate, orgnameengTemplate } from '../../../appconfig/DatatableTemplateSetting';
import { setRefresherCoursePublishStatus, setRefresherCourseStatus } from '../../../appconfig/AppHelper';

export const OnlineRefresherCourseList = () => {
    document.title = 'Online Refresher Course | GSPC';

    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    // Navigate
    const navigate = useNavigate();

    //Loading/Page Loading
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [courseList, setCourseList] = useState<any>([]);
    const [appStatusFilter, setAppStatusFilter] = useState<any>({});
    const [publishedStatusFilter, setPublishedStatusFilter] = useState<any>({});
    const [publishedExamStatusFilter, setPublishedExamStatusFilter] = useState<any>({});
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
    const [deleteId, setDeleteId] = useState<any>(null);

    // useEffect
    useEffect(() => {
        getOnlineRefresherCourseList();

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Get online refresher course list
    const getOnlineRefresherCourseList = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetails(GET_REFRESHER_COURSE_LIST);
            if (response) {
                const DataList: any = response;
                if (DataList.length === 0) {
                    setCourseList([]);
                } else {
                    setCourseList(DataList);
                }
                setPageLoad(true);
            } else {
                setPageLoad(false);
                setCourseList([]);
            }
        } catch (error: any) {
            setPageLoad(false);
            setCourseList([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry Could Not Load Data'
            });
        }
    };

    // Global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
                <Button label="Add Online Course" icon="pi pi-plus" onClick={() => navigate('/app/manage-refresher-course')} className="ml-2"></Button>
            </>
        );
    };

    //Clear All Filter
    const clearAll = () => {
        setGlobalFieldFilter(null);
        setAppStatusFilter(null);
        setPublishedStatusFilter(null);
        setPublishedExamStatusFilter(null);
    };

    // Search by course status
    const searchByCourseStatus = (options: any) => {
        return (
            <Dropdown
                filter
                value={appStatusFilter}
                onChange={(e: any) => {
                    setAppStatusFilter(e.value);
                    options.filterCallback(e.value.code, options.index);
                }}
                options={CourseStatusOptions}
                optionLabel="name"
                placeholder="Search By Status"
                className="order-drp"
            ></Dropdown>
        );
    };

    // Search by published status
    const searchByPublishedStatus = (options: any) => {
        return (
            <Dropdown
                filter
                value={publishedStatusFilter}
                onChange={(e: any) => {
                    setPublishedStatusFilter(e.value);
                    options.filterCallback(e.value.code, options.index);
                }}
                options={ActiveInactiveOptions}
                optionLabel="name"
                placeholder="Search By Status"
                className="order-drp"
            ></Dropdown>
        );
    };

    // Search by exam  published status
    const searchByExamPublishedStatus = (options: any) => {
        return (
            <Dropdown
                filter
                value={publishedExamStatusFilter}
                onChange={(e: any) => {
                    setPublishedExamStatusFilter(e.value);
                    options.filterCallback(e.value.code, options.index);
                }}
                options={ActiveInactiveOptions}
                optionLabel="name"
                placeholder="Search By Status"
                className="order-drp"
            ></Dropdown>
        );
    };
    // Right toolbar
    const rightToolbarTemplate = () => {
        return (
            <>
                <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAll()} className="p-button-outlined ml-2"></Button>
            </>
        );
    };

    // Template for booking status
    const bookingStatusTemplate = (rowData: any, rowIndex: any) => {
        let appStatusVal = setRefresherCourseStatus(rowData.status);
        return (
            <>
                <Badge severity={appStatusVal.severity} value={appStatusVal.value}></Badge>
            </>
        );
    };

    // Template for published status
    const publishedStatusTemplate = (rowData: any, rowIndex: any) => {
        let appStatusVal = setRefresherCoursePublishStatus(rowData.published_status);
        return (
            <>
                <Badge severity={appStatusVal.severity} value={appStatusVal.value}></Badge>
            </>
        );
    };

    // Template for exam published status
    const examPublishedStatusTemplate = (rowData: any, rowIndex: any) => {
        let appStatusVal = setRefresherCoursePublishStatus(rowData.quiz_published_status);
        return (
            <>
                <Badge severity={appStatusVal.severity} value={appStatusVal.value}></Badge>
            </>
        );
    };

    // Open delete modal
    const openDeleteModal = (id: any) => {
        setDeleteId(id);
        setDeleteModal(true);
    };

    // Close delete modal
    const closeDeleteModal = () => {
        setDeleteId(null);
        setDeleteModal(false);
    };

    // On delete api call
    const onDeleteApiCall = async () => {
        try {
            setDeleteLoader(true);
            // Delete api call
            const response = await deleteData(DELETE_REFRESHER_COURSE, deleteId);
            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response?.message
                });
                setDeleteLoader(false);
                setDeleteModal(false);
                setDeleteId(null);
                getOnlineRefresherCourseList();
                setPageLoad(true);
            } else {
                setPageLoad(false);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Something went wrong, Please try again.'
                });
                setDeleteLoader(false);
            }
        } catch (error: any) {
            if (error.response) {
                //Spinner False and Set Toast
                setDeleteLoader(false);
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong' });
            }
        }
    };

    // Template for actions
    const actionsTemplate = (rowData: any) => {
        return (
            <>
                <div className="actions action-btn">
                    <Button tooltip="View Course" tooltipOptions={{ position: 'top' }} icon="pi pi-eye" onClick={() => navigate(`/app/manage-refresher-course/${window.btoa(rowData.id)}`)} />
                    <Button tooltip="Booked Users" tooltipOptions={{ position: 'top' }} icon="far fa-address-book" onClick={() => navigate(`/app/refresher-course-candidates/${window.btoa(rowData.id)}`)} className="ml-2" />
                    <Button tooltip="View Certification" tooltipOptions={{ position: 'top' }} icon="far fa-file-alt" onClick={() => navigate(`/app/refresher-course-certification/${window.btoa(rowData.id)}`)} className="ml-2" />
                    {rowData?.status === "Completed" &&
                        new Date(rowData?.start_date) >=
                        new Date("2025-11-20") && (<Button tooltip="Co Member-Certificates" tooltipOptions={{ position: 'top' }} icon="pi pi-eye" onClick={() => navigate(`/app/refresher-course-list-certification/${window.btoa(rowData.id)}`)} className="ml-2" />)}

                    {rowData.published_status === 1 ? (
                        <>
                            <Tooltip target=".pro-to-original-url" position="top" />
                            <span className="pro-to-original-url" data-pr-tooltip="You can't delete this course because, course is active and booking started">
                                <Button icon="pi pi-trash" className="p-button-danger ml-2" disabled={true} />
                            </span>
                        </>
                    ) : (
                        <Button tooltip="Delete" tooltipOptions={{ position: 'top' }} icon="pi pi-trash" onClick={() => openDeleteModal(rowData.id)} className="p-button-danger ml-2" />
                    )}
                </div>
            </>
        );
    };

    return (
        <>
            <RefresherCourseTab mainTabLabel="Online Refresher Course" />
            <Toast ref={toast} />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    value={courseList}
                                    showGridlines
                                    stripedRows
                                    globalFilter={globalFilter}
                                    filters={globalFieldFilter}
                                    rows={defaultRowOptions}
                                    paginator={courseList.length >= 10 && true}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                    className="datatable-responsive p-datatable-sm"
                                    emptyMessage="No Courses Found"
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    removableSort
                                >
                                    {OnlineRefresherCourseColumnsList.map((col, i) => {
                                        if (col.field === 'action') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                        } else if (col.field === 'id') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={courseIdTemplate} />;
                                        } else if (col.field === 'start_date') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={courseStartTemplate} style={{ minWidth: '8rem' }} />;
                                        } else if (col.field === 'end_date') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={courseEndTemplate} style={{ minWidth: '8rem' }} />;
                                        } else if (col.field === 'orgnameeng') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={orgnameengTemplate} filter filterPlaceholder={'Search by ' + col.header} />;
                                        } else if (col.field === 'status') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={bookingStatusTemplate} filter filterElement={searchByCourseStatus} />;
                                        } else if (col.field === 'published_status') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={publishedStatusTemplate} filter filterElement={searchByPublishedStatus} />;
                                        } else if (col.field === 'quiz_published_status') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={examPublishedStatusTemplate} filter filterElement={searchByExamPublishedStatus} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {OnlineRefresherCourseColumnsList.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Dialog */}
            <Dialog
                visible={deleteModal}
                style={{ width: '450px' }}
                header="Delete Course"
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={closeDeleteModal} />
                        <Button label="Delete" icon="pi pi-trash" className="p-button-danger" onClick={onDeleteApiCall} loading={deleteLoader} />
                    </>
                }
                onHide={closeDeleteModal}
            >
                <div className="flex align-items-center justify-content-start">
                    <i className="pi pi-exclamation-triangle mr-3 delete-triangle" style={{ fontSize: '2rem' }} />
                    <span className="delete-dialog-note">Note: Once you delete this Course, This will be deleted permanently.</span>
                </div>
            </Dialog>
        </>
    );
};
